import React, { useCallback, useEffect, useRef, useState } from 'react';
import { collection, doc } from 'firebase/firestore';
import { Image, Loader2, Pencil, Plus, ToggleLeft, ToggleRight, Trophy } from 'lucide-react';
import { useAuth } from '../../../utils/AuthContext';
import { db } from '../../../utils/firebase';
import type { EmployeeAwardDefinition } from '../../../types';
import {
  createAwardDefinition,
  subscribeToAwardDefinitions,
  updateAwardDefinition,
  uploadAwardIconPng,
} from '../../../utils/employeeAchievements';
import SettingsSectionShell from '../SettingsSectionShell';
import './AchievementsSettingsSection.css';

type FormState = {
  name: string;
  description: string;
};

const emptyForm = (): FormState => ({ name: '', description: '' });

const AchievementsSettingsSection: React.FC = () => {
  const { user } = useAuth();
  const [definitions, setDefinitions] = useState<EmployeeAwardDefinition[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => subscribeToAwardDefinitions(setDefinitions), []);

  useEffect(() => {
    return () => {
      if (iconPreview?.startsWith('blob:')) URL.revokeObjectURL(iconPreview);
    };
  }, [iconPreview]);

  const resetForm = useCallback(() => {
    setForm(emptyForm());
    setIconFile(null);
    if (iconPreview?.startsWith('blob:')) URL.revokeObjectURL(iconPreview);
    setIconPreview(null);
    setEditingId(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [iconPreview]);

  const onPickIcon = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'image/png') {
      setError('Award icons must be PNG files.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('Award icon must be 2 MB or smaller.');
      return;
    }
    setError(null);
    setIconFile(file);
    if (iconPreview?.startsWith('blob:')) URL.revokeObjectURL(iconPreview);
    setIconPreview(URL.createObjectURL(file));
  };

  const startEdit = (def: EmployeeAwardDefinition) => {
    setEditingId(def.id ?? null);
    setForm({ name: def.name, description: def.description });
    setIconFile(null);
    if (iconPreview?.startsWith('blob:')) URL.revokeObjectURL(iconPreview);
    setIconPreview(def.iconUrl || null);
    setError(null);
    setMessage(null);
  };

  const handleSave = async () => {
    if (!user?.id) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      if (editingId) {
        const patch: Partial<EmployeeAwardDefinition> = {
          name: form.name,
          description: form.description,
        };
        if (iconFile) {
          const uploaded = await uploadAwardIconPng(iconFile, editingId);
          patch.iconUrl = uploaded.iconUrl;
          patch.iconStoragePath = uploaded.iconStoragePath;
        }
        await updateAwardDefinition(editingId, patch);
        setMessage('Award updated.');
      } else {
        if (!iconFile) throw new Error('Choose a PNG icon for this award.');
        const newId = doc(collection(db, 'employeeAwardDefinitions')).id;
        const uploaded = await uploadAwardIconPng(iconFile, newId);
        await createAwardDefinition({
          definitionId: newId,
          name: form.name,
          description: form.description,
          iconUrl: uploaded.iconUrl,
          iconStoragePath: uploaded.iconStoragePath,
          createdBy: user.id,
        });
        setMessage('Award created. Grant it from User Management.');
      }
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (def: EmployeeAwardDefinition) => {
    if (!def.id) return;
    setBusy(true);
    setError(null);
    try {
      await updateAwardDefinition(def.id, { isActive: !def.isActive });
      setMessage(def.isActive ? 'Award deactivated.' : 'Award reactivated.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed.');
    } finally {
      setBusy(false);
    }
  };

  if (user?.role !== 'admin') return null;

  const activeDefs = definitions.filter(d => d.isActive);
  const inactiveDefs = definitions.filter(d => !d.isActive);

  return (
    <SettingsSectionShell
      title="Certifications & Achievements"
      description="Define satirical awards and certifications. Grant them to employees from User Management."
    >
      {message && <p className="ach-settings-msg ach-settings-msg--ok">{message}</p>}
      {error && <p className="ach-settings-msg ach-settings-msg--err">{error}</p>}

      <section className="ach-settings-form-card">
        <h2 className="ach-settings-h2">
          {editingId ? (
            <>
              <Pencil size={16} aria-hidden /> Edit award
            </>
          ) : (
            <>
              <Plus size={16} aria-hidden /> Create award
            </>
          )}
        </h2>
        <div className="ach-settings-form-grid">
          <label className="ach-settings-field">
            <span>Name</span>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Certified Beam Whisperer"
              maxLength={80}
            />
          </label>
          <label className="ach-settings-field ach-settings-field--full">
            <span>Description</span>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Awarded for explaining mortise-and-tenon to a confused subcontractor."
              rows={3}
              maxLength={280}
            />
          </label>
          <div className="ach-settings-icon-row">
            <div className="ach-settings-icon-preview" aria-hidden>
              {iconPreview ? (
                <img src={iconPreview} alt="" />
              ) : (
                <Trophy size={28} strokeWidth={1.5} />
              )}
            </div>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png"
                className="ach-settings-file-input"
                onChange={onPickIcon}
              />
              <button
                type="button"
                className="pm-btn pm-btn-secondary pm-btn-sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
              >
                <Image size={14} aria-hidden />
                {iconPreview ? 'Replace PNG' : 'Upload PNG icon'}
              </button>
              <p className="ach-settings-hint">PNG only, max 2 MB. Square icons look best.</p>
            </div>
          </div>
        </div>
        <div className="ach-settings-form-actions">
          {editingId && (
            <button type="button" className="pm-btn pm-btn-secondary" onClick={resetForm} disabled={busy}>
              Cancel
            </button>
          )}
          <button
            type="button"
            className="pm-btn pm-btn-primary"
            onClick={handleSave}
            disabled={busy || !form.name.trim()}
          >
            {busy ? (
              <>
                <Loader2 size={14} className="ach-settings-spin" aria-hidden /> Saving…
              </>
            ) : editingId ? (
              'Save changes'
            ) : (
              'Create award'
            )}
          </button>
        </div>
      </section>

      <section className="ach-settings-list">
        <h2 className="ach-settings-h2">Active awards ({activeDefs.length})</h2>
        {activeDefs.length === 0 ? (
          <p className="ach-settings-empty">No active awards yet. Create one above.</p>
        ) : (
          <div className="ach-settings-cards">
            {activeDefs.map(def => (
              <article key={def.id} className="ach-settings-card">
                <img src={def.iconUrl} alt="" className="ach-settings-card-icon" />
                <div className="ach-settings-card-body">
                  <h3>{def.name}</h3>
                  <p>{def.description}</p>
                </div>
                <div className="ach-settings-card-actions">
                  <button type="button" className="pm-btn pm-btn-secondary pm-btn-sm" onClick={() => startEdit(def)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    className="pm-btn pm-btn-secondary pm-btn-sm"
                    onClick={() => toggleActive(def)}
                    disabled={busy}
                    title="Deactivate"
                  >
                    <ToggleRight size={14} aria-hidden />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {inactiveDefs.length > 0 && (
        <section className="ach-settings-list ach-settings-list--inactive">
          <h2 className="ach-settings-h2">Inactive ({inactiveDefs.length})</h2>
          <div className="ach-settings-cards">
            {inactiveDefs.map(def => (
              <article key={def.id} className="ach-settings-card ach-settings-card--inactive">
                <img src={def.iconUrl} alt="" className="ach-settings-card-icon" />
                <div className="ach-settings-card-body">
                  <h3>{def.name}</h3>
                  <p>{def.description}</p>
                </div>
                <div className="ach-settings-card-actions">
                  <button type="button" className="pm-btn pm-btn-secondary pm-btn-sm" onClick={() => startEdit(def)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    className="pm-btn pm-btn-secondary pm-btn-sm"
                    onClick={() => toggleActive(def)}
                    disabled={busy}
                    title="Reactivate"
                  >
                    <ToggleLeft size={14} aria-hidden />
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
    </SettingsSectionShell>
  );
};

export default AchievementsSettingsSection;
